/**
 * Предложения новых тем недельной сводки (#845, PR 5b): список для тех, кто
 * правит набор, и решения «Принять» / «Отклонить». Принятие меняет и набор
 * (новая активная тема, счёт капа), и предложения — после любого решения
 * перечитываются предложения и список набора; аналитика тем — только после
 * принятия.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  AcceptOrgThemeProposalResponse,
  RejectOrgThemeProposalResponse,
} from '@aica/api-types/schemas';
import { useAuth } from '@/contexts/UnifiedAuthContext';
import { retryTransientOnly } from '@/lib/query-retry';
import { invalidateVoiceThemeAnalytics, refreshVoiceThemeList } from './useVoiceThemes';

export const themeProposalKeys = {
  list: (orgId: string | null) => ['analytics', 'voice-theme-proposals', orgId] as const,
};

export function useThemeProposals(options: { enabled: boolean }) {
  const { apiClient, orgId } = useAuth();
  return useQuery({
    queryKey: themeProposalKeys.list(orgId),
    queryFn: () => apiClient.voiceThemes.proposals(),
    enabled: Boolean(orgId) && options.enabled,
    staleTime: 30 * 1000,
    retry: retryTransientOnly,
  });
}

export type ThemeProposalDecision = 'accept' | 'reject';

export function useDecideThemeProposal() {
  const { apiClient, orgId } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      decision,
    }: {
      id: string;
      decision: ThemeProposalDecision;
    }): Promise<AcceptOrgThemeProposalResponse | RejectOrgThemeProposalResponse> =>
      decision === 'accept'
        ? apiClient.voiceThemes.acceptProposal(id)
        : apiClient.voiceThemes.rejectProposal(id),
    // Only an accepted proposal changes the set (a new active theme): a rejected
    // one, or a refused decision, leaves the heavy themes analytics as it was.
    onSuccess: (_response, { decision }) => {
      if (decision === 'accept') invalidateVoiceThemeAnalytics(queryClient, orgId);
    },
    onSettled: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: themeProposalKeys.list(orgId) }),
        refreshVoiceThemeList(queryClient, orgId),
      ]),
  });
}
